import { addDays, differenceInCalendarDays, endOfDay, isAfter, isBefore, startOfDay, subDays } from 'date-fns'

import { isValidDate } from '../../helpers/date'

type ClampDateRangeToBoundsParams = {
  fromDate?: Date | null
  toDate?: Date | null
  minDate?: Date
  maxDate: Date
}

type ClampedDateRange = {
  fromDate?: Date | null
  toDate?: Date | null
  corrected: boolean
}

/**
 * Clamps a selected date range into the [minDate, maxDate] boundaries.
 *
 * When the whole range falls outside a boundary (e.g. the max selectable date drops into the
 * past once live events history loads for a game that stopped tracking), the range is shifted
 * to the nearest valid window while preserving its length in calendar days, instead of clamping
 * both edges onto the boundary and collapsing the selection to a one-day range (GR-2985).
 * When the span does not fit between minDate and maxDate, the range degrades to
 * [minDate, maxDate].
 *
 * Ranges that only partially cross a boundary keep the single-side clamping behavior: only the
 * out-of-bounds edge is moved, so valid user input is preserved during boundary changes such as
 * context switches or typing a year (GR-2459).
 */
export const clampDateRangeToBounds = ({ fromDate, toDate, minDate, maxDate }: ClampDateRangeToBoundsParams): ClampedDateRange => {
  // Shift the whole window, preserving its length, when the entire range is out of bounds
  if (fromDate && isValidDate(fromDate) && toDate && isValidDate(toDate)) {
    const span = Math.max(0, differenceInCalendarDays(toDate, fromDate))

    // Entirely after maxDate: shift back so the range ends at maxDate
    if (isAfter(startOfDay(fromDate), startOfDay(maxDate))) {
      const shiftedFrom = startOfDay(subDays(maxDate, span))
      const spanFits = !minDate || !isBefore(shiftedFrom, startOfDay(minDate))
      return {
        fromDate: spanFits ? shiftedFrom : startOfDay(minDate as Date),
        toDate: endOfDay(maxDate),
        corrected: true,
      }
    }

    // Entirely before minDate: shift forward so the range starts at minDate
    if (minDate && isBefore(startOfDay(toDate), startOfDay(minDate))) {
      const shiftedTo = endOfDay(addDays(minDate, span))
      const spanFits = !isAfter(startOfDay(shiftedTo), startOfDay(maxDate))
      return {
        fromDate: startOfDay(minDate),
        toDate: spanFits ? shiftedTo : endOfDay(maxDate),
        corrected: true,
      }
    }
  }

  // Partially out of bounds (or a single-edged range): clamp only the offending edge (GR-2459)
  let correctedFrom = fromDate
  let correctedTo = toDate
  let corrected = false

  // Clamp fromDate into [minDate, maxDate]
  if (correctedFrom && isValidDate(correctedFrom)) {
    if (minDate && isBefore(startOfDay(correctedFrom), startOfDay(minDate))) {
      correctedFrom = startOfDay(minDate)
      corrected = true
    }
    if (isAfter(startOfDay(correctedFrom), startOfDay(maxDate))) {
      correctedFrom = startOfDay(maxDate)
      corrected = true
    }
  }

  // Clamp toDate into [effectiveMin, maxDate] where effectiveMin
  // is the (possibly corrected) fromDate, falling back to minDate
  if (correctedTo && isValidDate(correctedTo)) {
    const effectiveMinDate = correctedFrom || minDate
    // If toDate is before the effective minimum, snap it forward
    if (effectiveMinDate && isBefore(startOfDay(correctedTo), startOfDay(effectiveMinDate))) {
      correctedTo = endOfDay(effectiveMinDate)
      corrected = true
    }
    // If toDate is after maxDate, snap it back
    if (isAfter(startOfDay(correctedTo), startOfDay(maxDate))) {
      correctedTo = endOfDay(maxDate)
      corrected = true
    }
  }

  return { fromDate: correctedFrom, toDate: correctedTo, corrected }
}
